import { motion } from "framer-motion";
import { Mail, MapPin, Globe, Linkedin, MessageSquare, CalendarCheck } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { CONTACT } from "../../config/contact";

const LINKEDIN_URL = CONTACT.linkedin;

const infoCards = [
  {
    icon: Mail,
    color: "#1F5EFF",
    label: "Sales Inquiries",
    value: CONTACT.salesEmail,
    href: `mailto:${CONTACT.salesEmail}`,
    testid: "contact-email-sales",
  },
  {
    icon: Mail,
    color: "#00B8A9",
    label: "General Inquiries",
    value: CONTACT.generalEmail,
    href: `mailto:${CONTACT.generalEmail}`,
    testid: "contact-email-general",
  },
  {
    icon: Globe,
    color: "#6D28D9",
    label: "Website",
    value: CONTACT.website,
    href: `https://${CONTACT.website}`,
    external: true,
    testid: "contact-website",
  },
  {
    icon: MapPin,
    color: "#0B1F3A",
    label: "Location",
    value: "India",
    testid: "contact-location",
  },
];

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="blob bg-[#1F5EFF]/20 h-[420px] w-[420px] -top-20 left-10" />
      <div className="blob bg-[#6D28D9]/20 h-[360px] w-[360px] bottom-0 right-20" />
      <div className="blob bg-[#00B8A9]/20 h-[300px] w-[300px] top-1/2 left-1/3" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="rounded-[2rem] border border-[#0B1F3A]/10 bg-white p-8 sm:p-14 shadow-[0_30px_80px_-50px_rgba(11,31,58,0.4)]">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="overline">Let&rsquo;s Talk</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A] tracking-tighter leading-[1.05]">
                Let&rsquo;s Build{" "}
                <span className="bg-gradient-to-r from-[#1F5EFF] via-[#6D28D9] to-[#00B8A9] bg-clip-text text-transparent">
                  Exceptional Content Experiences
                </span>{" "}
                Together.
              </h2>
              <p className="mt-5 text-[#475569] text-base sm:text-lg max-w-xl">
                Whether you&rsquo;re planning a publishing program, improving accessibility, modernizing legacy
                content, or accelerating digital transformation, Libros Consulting is ready to help.
              </p>
              <p className="mt-4 text-[#475569] text-base sm:text-lg max-w-xl">
                Partner with a trusted global team delivering scalable, high-quality content solutions with
                reliability, precision, and confidence.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid="contact-cta-schedule"
                  href={CONTACT.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Schedule a Consultation (opens in new tab)"
                  className="btn-primary"
                >
                  <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                  Schedule a Consultation
                </a>
                <a
                  data-testid="contact-cta-quote"
                  href={`mailto:${CONTACT.salesEmail}?subject=Free%20Quote%20Request`}
                  className="btn-ghost"
                >
                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                  Get a Free Quote
                </a>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-4">
                {infoCards.map((c) => {
                  const Icon = c.icon;
                  const inner = (
                    <>
                      <div className="flex items-center gap-3">
                        <div
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl shrink-0"
                          style={{ backgroundColor: `${c.color}14`, color: c.color }}
                        >
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <div className="overline text-[#475569]">{c.label}</div>
                      </div>
                      <div className="mt-3 font-display font-semibold text-[#0B1F3A] text-[14px] break-words [overflow-wrap:anywhere]">
                        {c.value}
                      </div>
                    </>
                  );
                  const cls =
                    "lift-card rounded-2xl border border-[#0B1F3A]/10 p-5 flex flex-col bg-[#F8FAFC]";
                  return c.href ? (
                    <a
                      key={c.label}
                      data-testid={c.testid}
                      href={c.href}
                      {...(c.external
                        ? { target: "_blank", rel: "noopener noreferrer", "aria-label": `${c.label}: ${c.value} (opens in new tab)` }
                        : {})}
                      className={cls}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div key={c.label} data-testid={c.testid} className={cls}>
                      {inner}
                    </div>
                  );
                })}
              </div>

              <a
                data-testid="contact-linkedin"
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with us on LinkedIn (opens in new tab)"
                className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-[#0B1F3A]/10 bg-[#F8FAFC] px-5 py-3 text-[14px] font-semibold text-[#0B1F3A] hover:border-[#1F5EFF]/40 hover:text-[#1F5EFF] transition-colors"
              >
                <Linkedin className="h-4 w-4 text-[#1F5EFF]" />
                Connect with us on LinkedIn
              </a>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6"
            >
              <div className="rounded-3xl bg-[#0B1F3A] text-white p-8 sm:p-9 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-[#1F5EFF]/30 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-[#6D28D9]/30 blur-3xl" />
                <div className="relative">
                  <div className="overline text-[#00B8A9]">Send us a message</div>
                  <h3 className="mt-3 mb-7 font-display text-2xl font-bold tracking-tight">
                    Tell us about your project.
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
